import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sprout, Grid, Scissors, HelpCircle, ArrowRight } from 'lucide-react';
import { fallbackServices } from '../data';
import ScrollReveal from './ScrollReveal';

const iconMap = {
  Sprout: Sprout,
  Grid: Grid,
  Scissors: Scissors
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setServices(data);
          } else {
            setServices(fallbackServices);
          }
        } else {
          setServices(fallbackServices);
        }
      } catch (error) {
        console.error('Fejl ved hentning af ydelser:', error);
        setServices(fallbackServices);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="ydelser" className="pt-12 pb-24 bg-white relative overflow-hidden border-t border-stone-100">
      {/* Decorative background elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-emerald-50/50 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-green-50/50 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <ScrollReveal variant="fade-up" duration={800} instant>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2 mb-4 tracking-wider uppercase">
              Jeg hjælper med dine haveprojekter
            </h2>
            <p className="text-stone-600 text-lg">
              Uanset om det er den store drøm om en ny have, professionelt flisearbejde eller blot den løbende vedligeholdelse, står jeg klar med rådgivning og solidt håndværk.
            </p>
          </div>
        </ScrollReveal>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon_name] || HelpCircle;
            const titleText = typeof service.title === 'object' ? service.title.da : service.title;
            const descriptionHtml = typeof service.description === 'object' ? service.description.da : service.description;
            const imageUrl = Array.isArray(service.image) ? service.image[0] : service.image;
            const slug = typeof service.slug === 'object' ? service.slug.da : service.slug;

            return (
              <ScrollReveal
                key={service.id || index}
                variant="fade-up"
                duration={800}
                delay={index * 150} // Staggered delay for each card
                className="h-full"
                instant
              >
                <div
                  className="relative group bg-white rounded-2xl border border-stone-200 hover:border-emerald-500/30 transition-all duration-300 hover:-translate-y-1.5 flex flex-col h-full shadow-sm hover:shadow-md"
                >
                  {/* Floating Icon - Centered and overlapping the top of the card */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-full shadow-xl border-4 border-white transition-transform duration-300 group-hover:scale-110 z-20">
                    <IconComponent className="h-8 w-8" />
                  </div>

                  {/* Service Image */}
                  <Link to={`/ydelser/${slug}`} className="relative h-56 rounded-t-2xl overflow-hidden block">
                    <img
                      src={imageUrl}
                      alt={titleText}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent opacity-40" />
                  </Link>

                  {/* Service Content */}
                  <div className="p-6 flex-grow flex flex-col">
                    <Link to={`/ydelser/${slug}`} className="block group/title">
                      <h3 className="text-base sm:text-lg font-bold text-stone-900 mb-2.5 line-clamp-1 md:line-clamp-2 lg:line-clamp-2 uppercase tracking-wide group-hover/title:text-emerald-600 transition-colors">
                        {titleText}
                      </h3>
                    </Link>
                    <div
                      className="text-stone-600 text-xs sm:text-sm leading-relaxed mb-4 flex-grow prose prose-sm prose-stone max-w-none line-clamp-4"
                      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                    
                    {/* Action Link */}
                    <Link
                      to={`/ydelser/${slug}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-stone-900 hover:text-emerald-600 transition-colors group/btn mt-auto self-start cursor-pointer"
                    >
                      Læs mere & få tilbud
                      <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Bottom CTA Box */}
        <ScrollReveal variant="scale-up" duration={800} delay={200} instant>
          <div className="mt-16 bg-emerald-900 border border-transparent rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg relative overflow-hidden">
            {/* Subtle background glow */}
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-emerald-800 rounded-full blur-2xl opacity-50 pointer-events-none" />
            
            <div className="max-w-2xl relative z-10">
              <h3 className="text-lg md:text-xl font-bold text-white mb-2 uppercase tracking-wide">
                Har du en speciel opgave, der ikke er nævnt her?
              </h3>
              <p className="text-white text-sm md:text-base">
                Tøv ikke med at kontakte mig. Jeg løser mange forskellige specialopgaver inden for anlæg og pleje og finder altid en god løsning.
              </p>
            </div>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="whitespace-nowrap bg-white hover:bg-emerald-50 text-emerald-900 font-semibold px-6 py-3.5 rounded-xl transition-all shadow-md hover:-translate-y-0.5 cursor-pointer relative z-10"
            >
              Kontakt mig for en snak
            </button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
